/**
 * The eight Wealth Series tiers on the paywall (TRAD-1316 / TRAD-1317), in
 * the order Product asked for. Copy comes from the dashboard prototype
 * (tradealgo-dashboard.vercel.app/wealth-series-signup); the v3 taglines and
 * the Core/Pulsar focus are interim until Product confirms them (Confluence
 * 1159987205, Q9/Q10), and the portfolio counts come from the v3 rename doc.
 *
 * Wordmarks are drawn as CSS masks filled with each tier's gradient, all at
 * the same cap height, so the eight logos line up whatever their artwork
 * does. Artwork: Atlas/Titan/Everest traced from the production PNGs; the v3
 * marks are the final files from attix-design-sg/wealth-series-logos
 * (Falcon option A, Quantum option B).
 */
import atlasLogo from "../../assets/images/wealth-series-signup/atlas-full.svg";
import titanLogo from "../../assets/images/wealth-series-signup/titan-full.svg";
import everestLogo from "../../assets/images/wealth-series-signup/everest-full.svg";
import pulsarLogo from "../../assets/images/wealth-series-signup/pulsar-logo-black.svg";
import rangerLogo from "../../assets/images/wealth-series-signup/ranger-logo-black.svg";
import falconLogo from "../../assets/images/wealth-series-signup/falcon-logo-black.svg";
import quantumLogo from "../../assets/images/wealth-series-signup/quantum-logo-black.svg";
import wsLockup from "../../assets/images/wealth-series-signup/wealth-series-horizontal.svg";

/** The plain "Contact Sales" button asks about the series as a whole. */
export const WS_PRODUCT_TYPE = "WEALTH SERIES";

/** Every wordmark is drawn at this cap height (px). */
const LOGO_CAP_HEIGHT = 15;

/** Cap height of the WS mark in the lockup, as a fraction of its height. */
export const WS_MARK_CAP = 0.71;

/**
 * Logo artwork: width/height of the SVG viewBox and the cap height of the
 * wordmark as a fraction of the artwork height (measured on the trimmed art).
 */
export const TIER_LOGO = {
  atlas: { src: atlasLogo, width: 2908, height: 576, cap: 0.8958 },
  titan: { src: titanLogo, width: 2720, height: 660, cap: 0.7696 },
  everest: { src: everestLogo, width: 4028, height: 576, cap: 0.8888 },
  pulsar: { src: pulsarLogo, width: 616.5, height: 100, cap: 1 },
  ranger: { src: rangerLogo, width: 680.8, height: 114.1, cap: 0.8945 },
  falcon: { src: falconLogo, width: 676, height: 114.1, cap: 0.8945 },
  quantum: { src: quantumLogo, width: 814.5, height: 114.1, cap: 0.8945 },
};

export const TIERS = [
  {
    id: "core",
    name: "Core",
    productType: "WEALTH SERIES CORE",
    tagline:
      "A straightforward investment approach focused on long-term growth.",
    focus: "Foundational",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #e9eef5, #9fb3cc)",
    glow: "#9fb3cc",
    href: "/wealth-series/core",
  },
  {
    id: "atlas",
    name: "Atlas",
    productType: "WEALTH SERIES ATLAS",
    tagline: "Powerful portfolios for steady and scalable success.",
    focus: "Growth",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #d7ebee, #a9cdd3)",
    glow: "#8fc4cc",
    href: "/wealth-series/atlas",
  },
  {
    id: "titan",
    name: "Titan",
    productType: "WEALTH SERIES TITAN",
    tagline: "Optimized portfolios designed to protect & amplify your wealth.",
    focus: "Balance",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #8ea5ef, #b9c4f5)",
    glow: "#8ea5ef",
    href: "/wealth-series/titan",
  },
  {
    id: "everest",
    name: "Everest",
    productType: "WEALTH SERIES EVEREST",
    tagline: "An exclusive solution for unparalleled wealth management.",
    focus: "Wealth management",
    portfolios: 5,
    gradient: "linear-gradient(90deg, #ffffff, #b58af0)",
    glow: "#b58af0",
    href: "/wealth-series/everest",
  },
  // Wealth Series v3 (TRAD-1317): no tier pages yet, so no href.
  {
    id: "pulsar",
    name: "Pulsar",
    productType: "WEALTH SERIES PULSAR",
    tagline:
      "Selected strategies from across the series, combined in one lineup.",
    focus: "Foundational",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #ffffff, #fcefd6)",
    glow: "#f2b544",
    href: null,
  },
  {
    id: "ranger",
    name: "Ranger",
    productType: "WEALTH SERIES RANGER",
    tagline: "Growth portfolios rebuilt on our latest AI models.",
    focus: "Growth",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #ffffff, #d8f4e6)",
    glow: "#4fcb8c",
    href: null,
  },
  {
    id: "falcon",
    name: "Falcon",
    productType: "WEALTH SERIES FALCON",
    tagline:
      "Balanced portfolios on our latest models, built to protect and grow.",
    focus: "Balance",
    portfolios: 3,
    gradient: "linear-gradient(90deg, #ffffff, #ffe0d9)",
    glow: "#ff7452",
    href: null,
  },
  {
    id: "quantum",
    name: "Quantum",
    productType: "WEALTH SERIES QUANTUM",
    tagline: "Our widest range of strategies for long-term wealth management.",
    focus: "Wealth management",
    portfolios: 5,
    gradient: "linear-gradient(90deg, #ffffff, #fbdcee)",
    glow: "#ee5fb0",
    href: null,
  },
];

const mask = (src, position, size) =>
  `url(${src}) ${position} / ${size} no-repeat`;

/** Inline style for a tier wordmark: masked artwork at the shared cap height. */
export const tierLogoStyle = (id) => {
  const { src, width, height, cap } = TIER_LOGO[id];
  const logoMask = mask(src, "left center", "contain");
  return {
    height: `${LOGO_CAP_HEIGHT / cap}px`,
    aspectRatio: `${width} / ${height}`,
    // Inline styles are not autoprefixed, so both forms are set.
    WebkitMask: logoMask,
    mask: logoMask,
  };
};

/** The WS mark inside the Core wordmark (the lockup's left part). */
export const wsMarkStyle = {
  height: `${LOGO_CAP_HEIGHT / WS_MARK_CAP}px`,
  WebkitMask: mask(wsLockup, "left center", "auto 100%"),
  mask: mask(wsLockup, "left center", "auto 100%"),
};

/** The hero lockup ("WS TradeAlgo Wealth Series"). */
export const lockupMaskStyle = {
  WebkitMask: mask(wsLockup, "center", "contain"),
  mask: mask(wsLockup, "center", "contain"),
};
